import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import {
  formatSecondsAsClockWithMs,
  parseClockToSeconds,
} from "../lib/clockHelpers";
import type { IneffectiveAction, Match } from "../types";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import { monotonicClockValue, type MatchClockSnapshot } from "./useMatchTimer";

type TeamChoice = "home" | "away" | "both";

type IneffectiveContext = {
  teamId?: string | null;
  playerId?: string | null;
  actionType?: IneffectiveAction | null;
  isNeutral?: boolean;
};

interface UseIneffectiveTimeParams {
  match: Match | null;
  selectedTeam: TeamChoice;
  /**
   * Ref (not a plain string) because the broadcast-cap main clock is derived
   * from `usePeriodManager`, which is wired up after this hook in
   * `CockpitContext.tsx` — a ref lets stoppage logging read the latest
   * value without requiring that ordering.
   */
  matchClockRef: MutableRefObject<string>;
  operatorPeriod: number;
  clockMode: "EFFECTIVE" | "INEFFECTIVE";
  /**
   * Returns whether the PATCH ultimately succeeded (after any internal
   * retries) — `false` once every attempt is exhausted. Callers that set
   * `activeIneffectiveContextRef` optimistically before invoking this MUST
   * check the resolved value and reconcile on `false`, or a permanently
   * failed switch leaves that ref stuck "active" forever, silently
   * swallowing every later stoppage via `beginIneffective`'s own
   * `if (activeIneffectiveContextRef.current) return` guard (PMA-COCK-155
   * lead review). Typed as `void | Promise<boolean>` rather than requiring
   * every caller/mock to return a promise.
   */
  handleModeSwitch: (
    mode: "EFFECTIVE" | "INEFFECTIVE",
  ) => void | Promise<boolean>;
  sendEvent: (event: Omit<MatchEvent, "match_id" | "timestamp">) => void;
  setIsBallInPlay: (value: boolean) => void;
  setMatch: Dispatch<SetStateAction<Match | null>>;
  /**
   * Live clock values from `useMatchTimer`. `optimisticModeChange` needs them
   * so the local match it writes carries the SAME derived clock fields that
   * `handleModeSwitch`'s PATCH is about to persist — see that function.
   */
  clockSnapshotRef: MutableRefObject<MatchClockSnapshot>;
}

export interface UseIneffectiveTimeResult {
  ineffectiveNoteOpen: boolean;
  setIneffectiveNoteOpen: Dispatch<SetStateAction<boolean>>;
  ineffectiveNoteText: string;
  setIneffectiveNoteText: Dispatch<SetStateAction<string>>;
  ineffectiveActionType: IneffectiveAction;
  setIneffectiveActionType: Dispatch<SetStateAction<IneffectiveAction>>;
  ineffectiveTeamSelection: "home" | "away";
  setIneffectiveTeamSelection: Dispatch<SetStateAction<"home" | "away">>;
  ineffectiveActionDropdownOpen: boolean;
  setIneffectiveActionDropdownOpen: Dispatch<SetStateAction<boolean>>;
  ineffectiveTeamDropdownOpen: boolean;
  setIneffectiveTeamDropdownOpen: Dispatch<SetStateAction<boolean>>;
  hasActiveIneffective: boolean;
  beginIneffective: (
    note?: string | null,
    context?: IneffectiveContext | null,
  ) => void;
  endIneffectiveIfNeeded: (nextMode: "EFFECTIVE") => void;
  confirmIneffectiveNote: () => void;
  cancelIneffectiveNote: () => void;
  logNeutralTimerEvent: (
    stoppageType: "VARStart" | "VARStop" | "TimeoutStart" | "TimeoutStop",
  ) => void;
}

export const useIneffectiveTime = ({
  match,
  selectedTeam,
  matchClockRef,
  operatorPeriod,
  clockMode,
  handleModeSwitch,
  sendEvent,
  setIsBallInPlay,
  setMatch,
  clockSnapshotRef,
}: UseIneffectiveTimeParams): UseIneffectiveTimeResult => {
  const [ineffectiveNoteOpen, setIneffectiveNoteOpen] = useState(false);
  const [ineffectiveNoteText, setIneffectiveNoteText] = useState("");
  const [ineffectiveActionType, setIneffectiveActionType] =
    useState<IneffectiveAction>("Other");
  const [ineffectiveTeamSelection, setIneffectiveTeamSelection] = useState<
    "home" | "away"
  >("home");
  const [ineffectiveActionDropdownOpen, setIneffectiveActionDropdownOpen] =
    useState(false);
  const [ineffectiveTeamDropdownOpen, setIneffectiveTeamDropdownOpen] =
    useState(false);
  const [hasActiveIneffective, setHasActiveIneffective] = useState(false);
  const [pendingIneffectiveContext, setPendingIneffectiveContext] =
    useState<IneffectiveContext | null>(null);
  const activeIneffectiveContextRef = useRef<
    (IneffectiveContext & { startedAtMs?: number }) | null
  >(null);
  const lastStoppageClockRef = useRef<string | null>(null);

  const getStoppageTeamId = useCallback(() => {
    if (!match) return null;
    if (selectedTeam === "away") return match.away_team.id;
    return match.home_team.id;
  }, [match, selectedTeam]);

  const resolveManualTeamSelection = useCallback(
    (teamId?: string | null): "home" | "away" => {
      if (teamId && teamId === match?.away_team.id) return "away";
      if (teamId && teamId === match?.home_team.id) return "home";
      return selectedTeam === "away" ? "away" : "home";
    },
    [match, selectedTeam],
  );

  const getManualTeamId = useCallback(
    (teamSelection: "home" | "away") => {
      if (teamSelection === "away") return match?.away_team.id ?? null;
      return match?.home_team.id ?? null;
    },
    [match],
  );

  const logClockStoppage = useCallback(
    (
      stoppageType: "ClockStop" | "ClockStart",
      notes?: string | null,
      context?: IneffectiveContext | null,
    ) => {
      if (!match) return;
      const normalizedTeamId = String(context?.teamId || "").toUpperCase();
      const isNeutralAction =
        context?.isNeutral ||
        normalizedTeamId === "NEUTRAL" ||
        context?.actionType === "VAR";
      // Note: "Referee" was removed from IneffectiveAction in v1.1.5;
      // referee stoppages are now attributed to "Other" and use teamId NEUTRAL
      // via the RefereeActionBar's logNeutralTimerEvent path.
      const resolvedTeamId = isNeutralAction
        ? "NEUTRAL"
        : context?.teamId || getStoppageTeamId();
      if (!resolvedTeamId) return;

      const baseClock = matchClockRef.current || "00:00.000";
      const adjustedClock =
        stoppageType === "ClockStart" &&
        lastStoppageClockRef.current === baseClock
          ? formatSecondsAsClockWithMs(parseClockToSeconds(baseClock) + 0.001)
          : baseClock;
      lastStoppageClockRef.current = adjustedClock;

      sendEvent({
        match_clock: adjustedClock,
        period: operatorPeriod,
        team_id: resolvedTeamId,
        type: "GameStoppage",
        data: {
          stoppage_type: stoppageType,
          reason: context?.actionType || "Other",
          trigger_action: context?.actionType || null,
          trigger_team_id: isNeutralAction ? null : context?.teamId || null,
          trigger_player_id: context?.playerId || null,
        },
        ...(notes ? { notes } : {}),
      });
    },
    [getStoppageTeamId, matchClockRef, match, operatorPeriod, sendEvent],
  );

  const logNeutralTimerEvent = useCallback(
    (stoppageType: "VARStart" | "VARStop" | "TimeoutStart" | "TimeoutStop") => {
      if (!match) return;
      const baseClock = matchClockRef.current || "00:00.000";
      const adjustedClock =
        lastStoppageClockRef.current === baseClock
          ? formatSecondsAsClockWithMs(parseClockToSeconds(baseClock) + 0.001)
          : baseClock;
      lastStoppageClockRef.current = adjustedClock;
      sendEvent({
        match_clock: adjustedClock,
        period: operatorPeriod,
        team_id: "NEUTRAL",
        type: "GameStoppage",
        data: {
          stoppage_type: stoppageType,
          reason:
            stoppageType === "TimeoutStart" || stoppageType === "TimeoutStop"
              ? "Timeout"
              : "VAR",
          trigger_action:
            stoppageType === "TimeoutStart" || stoppageType === "TimeoutStop"
              ? "Timeout"
              : "VAR",
          trigger_team_id: null,
          trigger_player_id: null,
        },
      });
    },
    [matchClockRef, match, operatorPeriod, sendEvent],
  );

  /**
   * Flip `match.clock_mode` locally so the cockpit reacts to a mode switch
   * without waiting for the PATCH + refetch round-trip.
   *
   * This MUST write the same derived clock fields `handleModeSwitch`
   * (useMatchTimer.ts) is about to persist. It used to write only
   * `clock_mode` / `last_mode_change_timestamp` and deliberately KEPT the old
   * `current_period_start_timestamp` — which made the clock jump on every
   * resume. `useMatchTimer`'s loop computes
   *
   *   effective = clock_seconds_at_period_start + (now - current_period_start_timestamp)
   *
   * so the instant `clock_mode` flipped back to EFFECTIVE with a
   * `current_period_start_timestamp` still pointing at the START of the
   * stoppage, that elapsed term was the WHOLE stoppage: press "Reanudar"
   * after a two-minute foul and the effective and global clocks leapt two
   * minutes forward, settling back only once the refetch landed. Reported
   * from the field as "cuando hay una falta y le doy reanudar brinca".
   *
   * The mirror image existed on the way in: entering INEFFECTIVE left
   * `match_time_seconds` at its last persisted value, so the frozen effective
   * clock dropped backwards by however long the clock had run since, and
   * leaving INEFFECTIVE dropped the ineffective total back to its last
   * persisted value.
   *
   * `monotonicClockValue` is applied to both, matching the server's own
   * guard (matches_new.MONOTONIC_CLOCK_FIELDS) so an optimistic write can
   * never rewind a clock either.
   */
  const optimisticModeChange = useCallback(
    (mode: "EFFECTIVE" | "INEFFECTIVE") => {
      const now = new Date().toISOString();
      const snapshot = clockSnapshotRef.current;
      setMatch((prev) => {
        if (!prev) return prev;
        const next: Match = {
          ...prev,
          clock_mode: mode,
          last_mode_change_timestamp: now,
          // Restart the elapsed-since baseline, exactly as the PATCH does.
          // Only when one already exists: a null here means the clock has
          // never been started, and inventing one would make the timer loop
          // believe a stopped match is running.
          current_period_start_timestamp: prev.current_period_start_timestamp
            ? now
            : prev.current_period_start_timestamp,
        };

        if (mode === "INEFFECTIVE") {
          // Leaving EFFECTIVE: freeze effective where it actually is.
          const nextEffective = monotonicClockValue(
            snapshot.effectiveSeconds,
            prev.clock_seconds_at_period_start ?? prev.match_time_seconds,
          );
          next.match_time_seconds = nextEffective;
          next.clock_seconds_at_period_start = nextEffective;
          // Mirrors handleModeSwitch's VAR carry-over: seconds counted into
          // the global clock during a VAR review live nowhere else, so
          // without this the global clock snaps back on VARStop.
          const varCarryOver = Math.round(snapshot.varSeconds);
          if (varCarryOver > 0) {
            next.ineffective_time_seconds = monotonicClockValue(
              snapshot.ineffectiveSeconds + varCarryOver,
              prev.ineffective_time_seconds,
            );
          }
        } else {
          // Leaving INEFFECTIVE: bank the ineffective seconds accrued during
          // the stoppage. `clock_seconds_at_period_start` is untouched — it
          // is the effective baseline the resumed clock counts up from.
          next.ineffective_time_seconds = monotonicClockValue(
            snapshot.ineffectiveSeconds,
            prev.ineffective_time_seconds,
          );
        }

        return next;
      });
    },
    [clockSnapshotRef, setMatch],
  );

  /**
   * `beginIneffective`/`confirmIneffectiveNote` set `activeIneffectiveContextRef`
   * BEFORE `handleModeSwitch("INEFFECTIVE")` resolves, optimistically. If
   * that PATCH exhausts every retry, `useMatchTimer` already reverts its own
   * `clockMode` and re-syncs `match` via `fetchMatch()` — but it has no
   * access to this hook's local ref/flag, so without this, a permanently
   * failed switch left `activeIneffectiveContextRef` stuck "active" forever,
   * and `beginIneffective`'s own `if (activeIneffectiveContextRef.current)
   * return` guard silently swallowed every subsequent stoppage attempt
   * (PMA-COCK-155 lead review).
   */
  const enterIneffectiveAndReconcile = useCallback(() => {
    void Promise.resolve(handleModeSwitch("INEFFECTIVE")).then((succeeded) => {
      if (succeeded === false) {
        activeIneffectiveContextRef.current = null;
        setHasActiveIneffective(false);
      }
    });
  }, [handleModeSwitch]);

  const beginIneffective = useCallback(
    (note?: string | null, context?: IneffectiveContext | null) => {
      if (clockMode === "INEFFECTIVE") return;
      if (activeIneffectiveContextRef.current) return;
      const trimmed = (note || "").trim();
      const fallbackTeamId =
        selectedTeam === "away"
          ? (match?.away_team.id ?? null)
          : selectedTeam === "home"
            ? (match?.home_team.id ?? null)
            : null;
      const explicitTeamId = context?.teamId ?? null;
      const resolvedContext = {
        teamId: explicitTeamId ?? fallbackTeamId,
        playerId: context?.playerId ?? null,
        actionType: context?.actionType ?? ineffectiveActionType,
        isNeutral: context?.isNeutral ?? false,
      };
      if (!trimmed) {
        setIneffectiveTeamSelection(
          resolveManualTeamSelection(resolvedContext.teamId),
        );
        if (resolvedContext.actionType) {
          setIneffectiveActionType(resolvedContext.actionType);
        }
        setIneffectiveNoteText("");
        setPendingIneffectiveContext({
          teamId: explicitTeamId,
          playerId: resolvedContext.playerId,
          actionType: context?.actionType ?? null,
          isNeutral: context?.isNeutral ?? false,
        });
        setIneffectiveNoteOpen(true);
        return;
      }
      logClockStoppage("ClockStop", trimmed, resolvedContext);
      activeIneffectiveContextRef.current = {
        ...resolvedContext,
        startedAtMs: Date.now(),
      };
      setHasActiveIneffective(true);
      optimisticModeChange("INEFFECTIVE");
      enterIneffectiveAndReconcile();
      setIsBallInPlay(false);
    },
    [
      clockMode,
      enterIneffectiveAndReconcile,
      ineffectiveActionType,
      logClockStoppage,
      match,
      optimisticModeChange,
      resolveManualTeamSelection,
      selectedTeam,
      setIsBallInPlay,
    ],
  );

  const endIneffectiveIfNeeded = useCallback(
    (nextMode: "EFFECTIVE") => {
      if (clockMode === "INEFFECTIVE" || activeIneffectiveContextRef.current) {
        logClockStoppage(
          "ClockStart",
          null,
          activeIneffectiveContextRef.current,
        );
        activeIneffectiveContextRef.current = null;
        setHasActiveIneffective(false);
      }
      optimisticModeChange(nextMode);
      handleModeSwitch(nextMode);
    },
    [clockMode, handleModeSwitch, logClockStoppage, optimisticModeChange],
  );

  const confirmIneffectiveNote = useCallback(() => {
    const trimmed = ineffectiveNoteText.trim();
    const fallbackTeamId = getManualTeamId(ineffectiveTeamSelection);
    const resolvedContext = {
      teamId: pendingIneffectiveContext?.teamId ?? fallbackTeamId,
      playerId: pendingIneffectiveContext?.playerId ?? null,
      actionType:
        pendingIneffectiveContext?.actionType ?? ineffectiveActionType,
    };
    logClockStoppage("ClockStop", trimmed || null, resolvedContext);
    activeIneffectiveContextRef.current = {
      ...resolvedContext,
      startedAtMs: Date.now(),
    };
    setHasActiveIneffective(true);
    optimisticModeChange("INEFFECTIVE");
    enterIneffectiveAndReconcile();
    setIsBallInPlay(false);
    setIneffectiveNoteOpen(false);
    setIneffectiveNoteText("");
    setPendingIneffectiveContext(null);
    setIneffectiveActionDropdownOpen(false);
    setIneffectiveTeamDropdownOpen(false);
  }, [
    enterIneffectiveAndReconcile,
    getManualTeamId,
    ineffectiveActionType,
    ineffectiveNoteText,
    ineffectiveTeamSelection,
    logClockStoppage,
    optimisticModeChange,
    pendingIneffectiveContext,
    setIsBallInPlay,
  ]);

  const cancelIneffectiveNote = useCallback(() => {
    setIneffectiveNoteOpen(false);
    setIneffectiveNoteText("");
    setPendingIneffectiveContext(null);
    setIneffectiveActionDropdownOpen(false);
    setIneffectiveTeamDropdownOpen(false);
  }, []);

  return {
    ineffectiveNoteOpen,
    setIneffectiveNoteOpen,
    ineffectiveNoteText,
    setIneffectiveNoteText,
    ineffectiveActionType,
    setIneffectiveActionType,
    ineffectiveTeamSelection,
    setIneffectiveTeamSelection,
    ineffectiveActionDropdownOpen,
    setIneffectiveActionDropdownOpen,
    ineffectiveTeamDropdownOpen,
    setIneffectiveTeamDropdownOpen,
    hasActiveIneffective,
    beginIneffective,
    endIneffectiveIfNeeded,
    confirmIneffectiveNote,
    cancelIneffectiveNote,
    logNeutralTimerEvent,
  };
};
